import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { glacierPolicyApiUpdateBypassValidatorsMutation } from '@/lib/heyapi/@tanstack/react-query.gen';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Message } from '../StandardMessage';
import { toast } from '@/components/ui/use-toast';

const BypassValidationDialog = ({
  isOpen,
  onOpenChange,
  isBypassing,
  setBypassing,
  setMessage,
  setLoading,
  policyQK,
  formulasQK,
  permissionsQK,
  policyId,
  bypassed,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isBypassing: boolean;
  setBypassing: (bypassing: boolean) => void;
  setMessage: (message: Message | undefined) => void;
  setLoading: (loading: boolean) => void;
  policyQK: any;
  formulasQK: any;
  permissionsQK?: any;
  policyId: string;
  bypassed?: boolean;
}) => {
  const queryClient = useQueryClient();

  const bypassValidation = useMutation({
    ...glacierPolicyApiUpdateBypassValidatorsMutation(),
    onSuccess(body) {
      setBypassing(false);
      queryClient.refetchQueries({ queryKey: policyQK });
      queryClient.refetchQueries({ queryKey: permissionsQK });
      if (body.bypass_validators) {
        setMessage({
          content:
            'Data Validation for current Transaction is bypassed successfully',
          level: 'INFO',
        });
      } else {
        setMessage({
          content: 'Data Validation is enabled for the current transaction',
          level: 'SUCCESS',
        });
      }
    },
    onError(error: any) {
      setLoading(false);
      setBypassing(false);
      queryClient.invalidateQueries({ queryKey: policyQK });
      queryClient.invalidateQueries({ queryKey: formulasQK });

      toast({
        title: 'Error',
        description: (
          <div className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <p className="text-white">{JSON.stringify(error?.detail)}</p>
          </div>
        ),
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {bypassed ? 'Enable' : 'Disable'} Validation
          </DialogTitle>
          <DialogDescription>
            {bypassed
              ? 'Required fields will be validated if you enable. Confirm if you wish to continue with validation.'
              : 'Required fields cannot be validated if you disable. Confirm if you wish to continue without validation.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isBypassing}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setBypassing(true);
              setMessage({
                content: bypassed
                  ? 'Enabling validation. Please wait...'
                  : 'Bypassing validation. Please wait...',
                level: 'INFO',
              });
              bypassValidation.mutate({
                body: {
                  bypassValidators: !bypassed,
                },
                path: { account_id: policyId },
              });
              onOpenChange(false);
            }}
            className="bg-brand-medium-green text-white hover:bg-brand-dark-green"
            disabled={isBypassing}
          >
            {bypassed
              ? `${isBypassing ? 'Enabling validation...' : 'Enable Validation'}`
              : `${isBypassing ? 'Bypassing validation...' : 'Bypass Validation'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BypassValidationDialog;
